/* eslint-disable react/no-array-index-key */
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Popup, PopupContent } from 'semantic-ui-react';
import api from '../../api';
import TextWithEllipsis from '../Extensions/TextWithEllipsis';
import './ReleaseNotes.css';

const getReleaseNotes = async (page, limit) => {
  const data = {
    page,
    limit,
  };

  try {
    const response = await api.getReleaseNotes(data);
    return response;
  } catch (err) {
    console.error('Error fetching release notes:', err);
    throw err;
  }
};

const getNoteDetail = async (key) => {
  try {
    const obj = {
      release_note_key: key,
    };

    const blobResponse = await api.downloadReleaseNoteFile(obj);

    const jsonText = await blobResponse.text();
    const data = JSON.parse(jsonText);

    return data;
  } catch (error) {
    console.error('Failed to fetch note detail from API:', key, error);
    throw error;
  }
};

class ReleaseNotes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      releaseNotes: [],
      page: 1,
      limit: 25,
      hasMore: true,
      showSeenAllMessage: false,
      isLoading: false,
      error: null,
      isInitialLoad: true,
      isPageLoading: false,
    };
    this.loaderRef = React.createRef();
    this.observer = null;
    this.messageTimer = null;
  }

  componentDidMount() {
    this.onUpdateLoading();
  }

  componentDidUpdate(prevProps, prevState) {
    const { isPageLoading } = this.state;

    if (prevState.isPageLoading !== isPageLoading && isPageLoading) {
      this.fetchReleaseNotes();
    }
  }

  componentWillUnmount() {
    if (this.messageTimer) {
      clearTimeout(this.messageTimer);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  onUpdateLoading = () => {
    this.setState({
      isPageLoading: true,
    });
  };

  handleObserver = (entities) => {
    const target = entities[0];
    const { hasMore, isLoading, isInitialLoad } = this.state;

    if (isInitialLoad) return;

    if (target.isIntersecting && hasMore && !isLoading) {
      this.fetchReleaseNotes();
    }
  };

  fetchReleaseNotes = async () => {
    const { t } = this.props;
    const { page, limit, isInitialLoad } = this.state;
    this.setState({ isLoading: true, error: null });

    try {
      const { response: newList, pagination } = await getReleaseNotes(
        page,
        limit
      );
      if (newList && newList.length > 0) {
        const detailPromises = newList.map((note) => {
          return getNoteDetail(note.key);
        });

        const results = await Promise.allSettled(detailPromises);

        const successfulOriginalNotes = [];
        const successfulDetails = [];

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            successfulOriginalNotes.push(newList[index]);
            successfulDetails.push(result.value);
          } else {
            console.error(
              `Failed to fetch detail for ${newList[index].url}:`,
              result.reason
            );
          }
        });

        const combinedNewNotes = successfulOriginalNotes.map((note, index) => {
          return {
            ...note,
            details: successfulDetails[index],
          };
        });

        this.setState(
          (prevState) => {
            return {
              releaseNotes: [...prevState.releaseNotes, ...combinedNewNotes],
              page: prevState.page + 1,
              hasMore: pagination.current < pagination.pages,
            };
          },
          () => {
            const { hasMore, releaseNotes } = this.state;
            if (!hasMore && releaseNotes.length > 0) {
              this.setState({ showSeenAllMessage: true });

              this.messageTimer = setTimeout(() => {
                this.setState({ showSeenAllMessage: false });
              }, 3000);
            }
            if (isInitialLoad && this.loaderRef.current) {
              this.observer = new IntersectionObserver(this.handleObserver, {
                threshold: 0.1,
              });
              this.observer.observe(this.loaderRef.current);
            }
          }
        );
      } else {
        this.setState({ hasMore: false });
      }
    } catch (err) {
      this.setState({ error: t('release_note.failed_load') });
      console.error(err);
    } finally {
      this.setState({
        isLoading: false,
        isInitialLoad: false,
      });
    }
  };

  render() {
    const {
      releaseNotes, isLoading, error, showSeenAllMessage
    } = this.state;

    const { t } = this.props;

    const badgeLabelMap = {
      'New Features': 'New Feature',
      Improvements: 'Improvement',
      'Bug Fixes': 'Bug Fix',
    };

    return (
      <div className="container-new">
        <div className="col-md-8 mx-auto">
          <div className="p-3 mb-4 border-bottom">
            <h1 className="f1-light">{t('release_note.title')}</h1>
          </div>

          <div id="release-notes">
            {releaseNotes.map((note) => {
              return (
                <section key={note.filename} className="release-note">
                  <header className="timeline-decorator">
                    <h2>
                      <span className="version-badge">
                        {note.details.clarity_version}
                      </span>
                      {note.details.oss_db_version && (
                        <Popup
                          trigger={(
                            <span className="db-version-badge">
                              DB
                              {' '}
                              {note.details.oss_db_version}
                            </span>
                          )}
                          inverted
                        >
                          <PopupContent>
                            {t('about.menu.oss_db_version')}
                          </PopupContent>
                        </Popup>
                      )}
                      <span className="release-date">
                        {new Date(note.details.release_date).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )}
                      </span>
                    </h2>
                  </header>

                  <ul className="change-log">
                    {note.details.changes.flatMap((change) => {
                      return change.description.map((desc, i) => {
                        const titleLower = change.title.toLowerCase();
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <li
                            key={`${change.title}-${i}`}
                            className="change-item"
                          >
                            <div
                              className={classNames('change-badge', {
                                'change-badge-new': titleLower.includes('new'),
                                'change-badge-improved':
                                  titleLower.includes('improvements'),
                                'change-badge-fixed':
                                  titleLower.includes('fix'),
                                'change-badge-removed':
                                  titleLower.includes('removed'),
                              })}
                            >
                              <TextWithEllipsis
                                as="span"
                                text={
                                  badgeLabelMap[change.title] || change.title
                                }
                                className="badge-text"
                              />
                            </div>
                            <div className="change-description">{desc}</div>
                          </li>
                        );
                      });
                    })}
                  </ul>
                </section>
              );
            })}
          </div>

          <div ref={this.loaderRef} className="loader-container">
            {isLoading && <p>{t('release_note.loading')}</p>}
            {showSeenAllMessage && <p>{t('release_note.seen_all')}</p>}
          </div>
          {error && <div className="error-container">{error}</div>}
        </div>
      </div>
    );
  }
}

export default withTranslation()(inject('state')(observer(ReleaseNotes)));
